/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";
// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import ArgonButton from "components/ArgonButton";
import { Chip } from "@mui/material";

// Data
import CreditMappingViewTableData from "Views/Activities/CreditMapping/data/creditMappingViewTableData";
import CreditMappingViewCreditTableData from "Views/Activities/CreditMapping/data/creditMappingViewCreditTableData";

// import ReferralTableData from "Views/Activities/Referral/data/ReferralTableData"
import { useState, useEffect } from "react";
import { set } from "date-fns";
import Dropdown from "components/CustomDropdown/Dropdown";
import ArgonInput from "components/ArgonInput";
import { useParams, useNavigate } from "react-router-dom";
import api from "utils/api";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PropTypes from "prop-types";

function Tables({ classData }) {
  const [cycle, setCycle] = useState("");

  const toSafeNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const resolveCycleBySelection = (cycles, selectedCycleName) => {
    if (!Array.isArray(cycles) || cycles.length === 0) return null;
    if (!selectedCycleName) return cycles[0] || null;

    const normalizedSelected = String(selectedCycleName).trim().toLowerCase();
    return (
      cycles.find(
        (item) =>
          String(item?.name || "")
            .trim()
            .toLowerCase() === normalizedSelected
      ) ||
      cycles.find((item, index) => String(index + 1) === normalizedSelected) ||
      cycles[0] ||
      null
    );
  };

  const getCycleSessions = (cycleData) =>
    toSafeNumber(cycleData?.totalNoOfClasses || cycleData?.classes || cycleData?.sessionCount || 0);

  const cycles = Array.isArray(classData?.cycles) ? classData.cycles : [];
  const totalCycles = cycles.length;
  const selectedCycleData = resolveCycleBySelection(cycles, cycle) || cycles[0] || null;

  const getTotalCycleAmount = (cycles, fieldName, fallbackValue = 0) => {
    if (!Array.isArray(cycles) || cycles.length === 0) return toSafeNumber(fallbackValue);
    const total = cycles.reduce((sum, item) => sum + toSafeNumber(item?.[fieldName]), 0);
    return total > 0 ? total : toSafeNumber(fallbackValue);
  };

  useEffect(() => {
    if (cycles.length > 0 && !cycle) {
      const currentCycle =
        classData?.currentCycle ||
        classData?.currentCycleName ||
        cycles.find((item) => item?.isCurrent)?.name ||
        cycles.find((item) => item?.status === "ONGOING")?.name ||
        cycles[0]?.name ||
        "Cycle 1";

      setCycle(currentCycle);
    }
  }, [cycles, cycle, classData]);

  const completedSessions = toSafeNumber(selectedCycleData?.completedClasses);

  const totalSessions = toSafeNumber(
    selectedCycleData?.totalNoOfClasses ||
      selectedCycleData?.classes ||
      selectedCycleData?.sessionCount ||
      classData?.totalNoOfClasses ||
      classData?.totalClasses
  );

  const normalizePaymentStatus = (status) => {
    const value = String(status || "")
      .trim()
      .toUpperCase();

    if (!value) return "PENDING";

    if (value === "SUCCESS") return "PAID";
    if (value === "COMPLETED") return "PAID";

    if (["ONGOING", "UPCOMING", "CREATED", "SCHEDULED"].includes(value)) {
      return "PENDING";
    }

    return value;
  };

  const getCycleNumber = (cycleName, index) => {
    const match = String(cycleName || "").match(/\d+/);
    return match ? Number(match[0]) : index + 1;
  };

  const normalizedInvoices = Array.isArray(classData?.invoices)
    ? [...classData.invoices].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    : [];

  const cycleIndex = cycles.findIndex(
    (item) =>
      String(item?.name || "")
        .trim()
        .toLowerCase() ===
      String(selectedCycleData?.name || "")
        .trim()
        .toLowerCase()
  );

  const safeCycleIndex = cycleIndex >= 0 ? cycleIndex : 0;

  const selectedInvoice =
    normalizedInvoices.find(
      (invoice) =>
        Number(invoice?.cycleNumber) === getCycleNumber(selectedCycleData?.name, safeCycleIndex) ||
        String(invoice?.cycleName || "")
          .trim()
          .toLowerCase() ===
          String(selectedCycleData?.name || "")
            .trim()
            .toLowerCase()
    ) ||
    normalizedInvoices[safeCycleIndex] ||
    null;
  const selectedCycleAmount = toSafeNumber(
    selectedCycleData?.monthlyFee ||
      selectedCycleData?.paymentAmount ||
      selectedInvoice?.totalAmount ||
      classData?.paymentAmount
  );

  const selectedPaymentStatus = normalizePaymentStatus(
    selectedInvoice?.status ||
      selectedCycleData?.paymentStatus ||
      selectedCycleData?.financeStatus ||
      selectedCycleData?.feeStatus ||
      selectedCycleData?.invoiceStatus ||
      classData?.paymentStatus
  );

  console.log("FULL classData:", classData);
  console.log("Invoices:", normalizedInvoices);
  console.log("Cycle Index:", safeCycleIndex);
  console.log("Selected Cycle:", selectedCycleData);
  console.log("Selected Invoice:", selectedInvoice);
  console.log("Selected Payment Status:", selectedPaymentStatus);
  const progress = totalSessions > 0 ? (completedSessions / totalSessions) * 100 : 0;

  const usedAmount =
    totalSessions > 0 ? Math.round((completedSessions / totalSessions) * selectedCycleAmount) : 0;

  const balanceAmount = Math.max(0, selectedCycleAmount - usedAmount);

  // Ensure progress is a safe bounded integer and compute SVG circle metrics
  const safeProgress = Math.max(0, Math.min(100, Math.round(progress)));
  const CIRCLE_R = 46; // matches the SVG arc radius used in path d
  const circumference = 2 * Math.PI * CIRCLE_R;
  const strokeDasharray = `${circumference}px`;
  const strokeDashoffset = `${circumference * (1 - safeProgress / 100)}px`;

  const selectedCycleSessions = getCycleSessions(selectedCycleData) || totalSessions;
  const selectedCycleMonthlyFee = toSafeNumber(selectedCycleData?.monthlyFee);
  const selectedCycleTutorPay = toSafeNumber(selectedCycleData?.tutorPay);

  const oldTutorDetails = classData?.oldTutorDetails || null;
  const oldTutorName = oldTutorDetails?.fullName || oldTutorDetails?.name || "N/A";
  const oldTutorSalary = classData?.oldTutorPerClassSalary ?? 0;
  const oldTutorTotalSalary = classData?.oldTutorCalculatedAmount ?? 0;
  const oldTutorCompletedClasses = classData?.oldTutorCompletedClasses ?? 0;
  const oldTutorSalaryStatus = classData?.oldTutorSalaryStatus || "N/A";
  const oldTutorReplacedAt = classData?.oldTutorReplacedAt || null;
  const oldTutorReplacedDateLabel = oldTutorReplacedAt
    ? new Date(oldTutorReplacedAt).toLocaleDateString()
    : "N/A";
  const feeCharge =
    selectedCycleMonthlyFee > 0
      ? selectedCycleMonthlyFee / Math.max(selectedCycleSessions, 1)
      : toSafeNumber(classData?.feeCharge) ||
        (toSafeNumber(classData?.paymentAmount) > 0 && totalSessions > 0
          ? toSafeNumber(classData?.paymentAmount) / totalSessions
          : 0);
  console.log("Calculated Fee Charge per class:", feeCharge);

  const tutorSalary =
    toSafeNumber(classData?.tutorSalary) ||
    toSafeNumber(classData?.tutorPayRate) ||
    (selectedCycleTutorPay > 0 ? selectedCycleTutorPay / Math.max(selectedCycleSessions, 1) : 0);

  const getInvoiceForCycle = (cycleData, cycleIdx) =>
    normalizedInvoices.find(
      (invoice) =>
        Number(invoice?.cycleNumber) === getCycleNumber(cycleData?.name, cycleIdx) ||
        String(invoice?.cycleName || "")
          .trim()
          .toLowerCase() ===
          String(cycleData?.name || "")
            .trim()
            .toLowerCase()
    ) ||
    normalizedInvoices[cycleIdx] ||
    null;

  const getCycleCharge = (cycleData, cycleIdx) => {
    const cycleClasses = getCycleSessions(cycleData);
    const matchingInvoice = getInvoiceForCycle(cycleData, cycleIdx);
    return Math.round(
      toSafeNumber(cycleData?.monthlyFee) ||
        toSafeNumber(matchingInvoice?.totalAmount) ||
        (cycleClasses > 0 ? feeCharge * cycleClasses : 0)
    );
  };

  const getCycleTutorPay = (cycleData, cycleIdx) => {
    const cycleClasses = getCycleSessions(cycleData);
    const fallbackTutorPay = Math.round(
      toSafeNumber(cycleData?.tutorPay) || (cycleClasses > 0 ? cycleClasses * tutorSalary : 0)
    );

    const cycleNumber = getCycleNumber(cycleData?.name, cycleIdx);
    const oldTutorCyclesForThisCycle = Array.isArray(classData?.oldTutorPaymentCycles)
      ? classData.oldTutorPaymentCycles.filter(
          (paymentCycle) => Number(paymentCycle?.cycleNumber) === cycleNumber
        )
      : [];

    if (oldTutorCyclesForThisCycle.length > 0) {
      const oldTutorClassesInCycle = oldTutorCyclesForThisCycle.reduce(
        (sum, paymentCycle) => sum + toSafeNumber(paymentCycle?.completedClasses),
        0
      );
      const oldTutorPayInCycle = oldTutorCyclesForThisCycle.reduce((sum, paymentCycle) => {
        const calculatedAmount = toSafeNumber(paymentCycle?.calculatedAmount);
        return (
          sum +
          (calculatedAmount > 0
            ? calculatedAmount
            : toSafeNumber(paymentCycle?.tutorPayRate) *
              toSafeNumber(paymentCycle?.completedClasses))
        );
      }, 0);
      const remainingClasses = Math.max(cycleClasses - oldTutorClassesInCycle, 0);

      return Math.round(oldTutorPayInCycle + remainingClasses * tutorSalary);
    }

    return fallbackTutorPay;
  };
  const totalAmount = Math.round(
    toSafeNumber(classData?.totalAmount) ||
      getTotalCycleAmount(cycles, "monthlyFee", 0) ||
      toSafeNumber(classData?.paymentAmount)
  );

  // Calculate sessions
  const sessionsPerWeek = classData?.numberOfClassesInAWeek || classData?.weeklyClasses || 0;
  const hoursPerSession = classData?.duration ? classData.duration / 60 : 1.5;
  const totalClasses = classData?.totalNoOfClasses || classData?.totalClasses || 0;
  const noOfMonths = classData?.noOfMonths || 1;
  const sessionsPerMonth = noOfMonths > 0 ? Math.round(totalClasses / noOfMonths) : 0;

  const totalPackageFee =
    cycles.length > 0
      ? cycles.reduce((sum, cycleItem, idx) => sum + getCycleCharge(cycleItem, idx), 0)
      : totalAmount;
  const totalTutorSalary =
    cycles.length > 0
      ? cycles.reduce((sum, cycleItem, idx) => sum + getCycleTutorPay(cycleItem, idx), 0)
      : Math.round(
          toSafeNumber(classData?.totalTutorCost) ||
            toSafeNumber(classData?.totalTutorPay) ||
            totalClasses * tutorSalary
        );
  const totalProfit = totalPackageFee - totalTutorSalary;
  const totalProfitPercent =
    totalPackageFee > 0 ? Math.round((totalProfit / totalPackageFee) * 100) : 0;

  const monthPackageFee = getCycleCharge(selectedCycleData, safeCycleIndex);
  const monthTutorSalary = getCycleTutorPay(selectedCycleData, safeCycleIndex);
  const monthProfit = monthPackageFee - monthTutorSalary;
  const monthProfitPercent =
    monthPackageFee > 0 ? Math.round((monthProfit / monthPackageFee) * 100) : 0;

  const classStatus = classData?.status || "N/A";
  const tutorName = classData?.tutor?.fullName || classData?.tutor?.name || "N/A";

  // Match the parent's fee to the exact number of classes taught by the old tutor.
  const oldTutorClassesFee = feeCharge * oldTutorCompletedClasses;
  const oldTutorMargin =
    oldTutorClassesFee > 0
      ? Math.round(((oldTutorClassesFee - oldTutorTotalSalary) / oldTutorClassesFee) * 100)
      : null;
  const hasOldTutorReplacement = Boolean(
    oldTutorReplacedAt || oldTutorDetails?.fullName || classData?.oldTutorId
  );

  // Get student data for table
  const studentsData = classData?.students || [];
  console.log("Students Data in Credit Mapping:", studentsData);
  console.log("Total Students:", studentsData.length);

  const { columns } = CreditMappingViewTableData;

  const formatPreferredTime = (student) => {
    const preferredTime =
      student?.preferredTime ||
      student?.preferredTimeSlot?.name ||
      student?.preferredTimeSlot?.label ||
      student?.preferredSlot?.name ||
      classData?.preferredTime;

    if (preferredTime) return preferredTime;

    const duration =
      student?.preferredTimeDuration || student?.duration || classData?.duration || null;

    return duration ? `${duration} mins` : "N/A";
  };

  const formatTuitionTenure = (student) => {
    const classesPerWeek =
      student?.noOfClassesPerWeek ||
      student?.numberOfClassesInAWeek ||
      classData?.numberOfClassesInAWeek;

    if (classesPerWeek) return `${classesPerWeek} classes/week`;

    if (student?.tuitionTenure) return student.tuitionTenure;

    const months = student?.noOfMonths || classData?.noOfMonths;
    return months ? `${months} months` : "N/A";
  };

  // Generate dynamic rows from studentsData
  const rows = studentsData.map((student, index) => ({
    Sno: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {index + 1}
      </ArgonTypography>
    ),
    "Student name": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student?.name || student?.fullName || "N/A"}
      </ArgonTypography>
    ),
    Class: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student?.class?.name || student?.className || classData?.className || "N/A"}
      </ArgonTypography>
    ),
    subject: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student?.subjects && student.subjects.length > 0
          ? student.subjects.map((sub) => sub.name || sub).join(", ")
          : "N/A"}
      </ArgonTypography>
    ),
    Gender: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student?.gender || "N/A"}
      </ArgonTypography>
    ),
    "Date of Birth": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student?.dob || student?.dateOfBirth
          ? new Date(student.dob || student.dateOfBirth).toLocaleDateString()
          : "N/A"}
      </ArgonTypography>
    ),
    "School/college/university Name": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student?.schoolName || student?.school?.name || student?.collegeName || "N/A"}
      </ArgonTypography>
    ),
    Board: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student?.board?.name || student?.boardName || classData?.board?.name || "N/A"}
      </ArgonTypography>
    ),
    "Preferred Time": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {formatPreferredTime(student)}
      </ArgonTypography>
    ),
    "Tuition Tenure": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {formatTuitionTenure(student)}
      </ArgonTypography>
    ),
    "Location City": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student?.parentId?.city || student?.locationCity || classData?.parent?.city || "N/A"}
      </ArgonTypography>
    ),
    "Tutors Level": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {classData?.tutor?.tutorLevel || student?.tutorLevel || "N/A"}
      </ArgonTypography>
    ),
  }));

  const { columns: columns1 } = CreditMappingViewCreditTableData;

  const rows1 = cycles.map((cycle, index) => ({
    "Cycle Name": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {cycle.name || `Cycle ${index + 1}`}
      </ArgonTypography>
    ),
    "Total Classes": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {cycle.totalNoOfClasses || 0}
      </ArgonTypography>
    ),
    "Completed Classes": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {cycle.completedClasses || 0}
      </ArgonTypography>
    ),
    Status: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {cycle.status || "N/A"}
      </ArgonTypography>
    ),
  }));

  return (
    <ArgonBox py={3}>
      <ArgonBox mb={3}>
        <Card>
          <ArgonBox p={3}>
            <Card
              sx={{
                mt: 3,
                borderRadius: "20px",
                backgroundColor: "white",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                border: "1px solid #f5f5f5",
              }}
            >
              <ArgonBox px={3} py={1.5}>
                {" "}
                {/* 👈 yaha py kam kiya */}
                <ArgonBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexDirection="row"
                  gap={4}
                >
                  {/* Class Status */}
                  <ArgonBox display="flex" alignItems="center" flexDirection="row">
                    <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                      Class Status
                    </ArgonTypography>
                    <ArgonBox
                      ml={2}
                      px={2.5}
                      py={0.5}
                      sx={{
                        background:
                          classStatus === "CREATED"
                            ? "linear-gradient(135deg, #9e9e9e, #757575)"
                            : classStatus === "SCHEDULED"
                            ? "linear-gradient(135deg, #2196f3, #1976d2)"
                            : classStatus === "ONGOING"
                            ? "linear-gradient(135deg, #ff9800, #f57c00)"
                            : classStatus === "CANCELLED"
                            ? "linear-gradient(135deg, #f44336, #d32f2f)"
                            : classStatus === "COMPLETED"
                            ? "linear-gradient(135deg, #43a047, #26a69a)"
                            : classStatus === "ON_HOLD"
                            ? "linear-gradient(135deg, #ff5722, #e64a19)"
                            : "linear-gradient(135deg, #f44336, #d32f2f)",
                        borderRadius: "20px",
                        boxShadow: "0 4px 10px rgba(67,160,71,0.3)",
                      }}
                    >
                      <ArgonTypography
                        variant="caption"
                        fontWeight="bold"
                        color="white"
                        sx={{ fontSize: "13px", letterSpacing: "0.5px" }}
                      >
                        {classStatus}
                      </ArgonTypography>
                    </ArgonBox>
                  </ArgonBox>

                  {/* Tutor Name */}
                  <ArgonBox>
                    <ArgonTypography variant="body2" fontWeight="medium" color="text">
                      Tutor&apos;s Name
                    </ArgonTypography>
                    <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                      {tutorName}
                    </ArgonTypography>
                  </ArgonBox>

                  {/* Cycle Dropdown */}
                  <ArgonBox display="flex" alignItems="center" flexDirection="row">
                    <ArgonTypography variant="body2" fontWeight="medium" mr={2}>
                      Cycle
                    </ArgonTypography>
                    <Dropdown
                      label=""
                      options={cycles.map((cycle, index) => cycle.name || `Cycle ${index + 1}`)}
                      value={cycle}
                      onChange={(e) => setCycle(e.target.value)}
                      sx={{
                        minWidth: "100px",
                        borderRadius: "12px",
                        backgroundColor: "#fafafa",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      }}
                    />
                  </ArgonBox>
                </ArgonBox>
              </ArgonBox>
            </Card>
          </ArgonBox>

          <ArgonBox>
            <ArgonTypography sx={{ mx: 2, mb: 3 }} variant="h5" fontWeight="medium">
              Students List
            </ArgonTypography>
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              <Table columns={columns} rows={rows} />
            </ArgonBox>
          </ArgonBox>

          <Card
            sx={{
              mt: 3,
              borderRadius: "16px",
              backgroundColor: "white",
              boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
              border: "1px solid #f0f0f0",
            }}
          >
            <ArgonBox p={3}>
              {/* Header */}
              <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
                <ArgonBox
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    p: 1.2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
                  }}
                >
                  {/* <Payment sx={{ color: "#344767", fontSize: "22px" }} /> */}
                </ArgonBox>
                <ArgonBox>
                  <ArgonTypography variant="h5" fontWeight="bold" color="dark">
                    Package Details
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="text">
                    Pricing and session information
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              <Grid container spacing={3}>
                {/* Hourly Rates */}
                <Grid item xs={12} md={3}>
                  <ArgonBox
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "12px",
                      p: 2.5,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      border: "1px solid #f5f5f5",
                      height: "100%",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                      {/* <AttachMoney sx={{ color: "#4caf50", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        Class Rates
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox>
                      <ArgonBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Fee Charge per class
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                          ₹ {Math.round(feeCharge)}
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Tutor Salary per class
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                          ₹ {Math.round(tutorSalary)}
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Sessions */}
                <Grid item xs={12} md={3}>
                  <ArgonBox
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "12px",
                      p: 2.5,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      border: "1px solid #f5f5f5",
                      height: "100%",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                      {/* <Schedule sx={{ color: "#2196f3", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        Sessions
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox>
                      <ArgonBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Sessions in a week
                        </ArgonTypography>
                        <ArgonTypography variant="caption" fontWeight="medium" color="dark">
                          {sessionsPerWeek} sessions | {sessionsPerWeek * hoursPerSession} hours
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Sessions in a month
                        </ArgonTypography>
                        <ArgonTypography variant="caption" fontWeight="medium" color="dark">
                          {sessionsPerMonth} sessions | {sessionsPerMonth * hoursPerSession} hours
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Month Package Summary */}
                <Grid item xs={12} md={3}>
                  <ArgonBox
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "12px",
                      p: 2.5,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      border: "1px solid #f5f5f5",
                      height: "100%",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        Month Package Summary
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox>
                      <ArgonBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Total Fee
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                          ₹ {monthPackageFee}
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Tutor Pay
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                          ₹ {monthTutorSalary}
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Profit
                        </ArgonTypography>
                        <ArgonTypography
                          variant="body2"
                          fontWeight="bold"
                          color={
                            monthProfitPercent < 20
                              ? "error"
                              : monthProfitPercent <= 25
                              ? "warning"
                              : "success"
                          }
                          sx={{
                            color:
                              monthProfitPercent >= 20 && monthProfitPercent <= 25
                                ? "#E6A700 !important"
                                : undefined,
                          }}
                        >
                          ₹ {monthProfit} ({monthProfitPercent}%)
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Total Package Summary */}
                <Grid item xs={12} md={3}>
                  <ArgonBox
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "12px",
                      p: 2.5,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      border: "1px solid #f5f5f5",
                      height: "100%",
                    }}
                  >
                    <ArgonBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1.5}
                      mb={2}
                    >
                      {/* <Payment sx={{ color: "#344767", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        Total Package Summary
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        Total Cycles: {totalCycles}
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox>
                      <ArgonBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <ArgonTypography variant="body2" color="text" fontWeight="bold">
                          Total Fee
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                          ₹ {Math.round(totalPackageFee)}
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Tutor Pay
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                          ₹ {Math.round(totalTutorSalary)}
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Profit
                        </ArgonTypography>
                        <ArgonTypography
                          variant="body2"
                          fontWeight="bold"
                          color={
                            totalProfitPercent < 20
                              ? "error"
                              : totalProfitPercent <= 25
                              ? "warning"
                              : "success"
                          }
                          sx={{
                            color:
                              totalProfitPercent >= 20 && totalProfitPercent <= 25
                                ? "#E6A700 !important"
                                : undefined,
                          }}
                        >
                          ₹ {Math.round(totalProfit)} ({totalProfitPercent}%)
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>
              </Grid>

              {/* Tutor Info */}
              <ArgonBox mt={3}>
                <ArgonTypography variant="body2" fontWeight="bold" color="dark">
                  Tutor Name - {tutorName}
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>
          </Card>

          {hasOldTutorReplacement && (
            <Card
              sx={{
                mt: 3,
                borderRadius: "16px",
                backgroundColor: "white",
                boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                border: "1px solid #f0f0f0",
              }}
            >
              <ArgonBox p={3}>
                <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
                  <ArgonBox>
                    <ArgonTypography variant="h5" fontWeight="bold" color="dark">
                      Old Tutor Details
                    </ArgonTypography>
                    <ArgonTypography variant="body2" color="text">
                      Split data for replaced tutor
                    </ArgonTypography>
                  </ArgonBox>
                </ArgonBox>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <ArgonBox
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        p: 2.5,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                        border: "1px solid #f5f5f5",
                        height: "100%",
                      }}
                    >
                      <ArgonTypography variant="body2" color="text" fontWeight="medium">
                        Old Tutor Name
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        {oldTutorName}
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <ArgonBox
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        p: 2.5,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                        border: "1px solid #f5f5f5",
                        height: "100%",
                      }}
                    >
                      <ArgonTypography variant="body2" color="text" fontWeight="medium">
                        Old Tutor Salary / class
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        ₹ {Math.round(oldTutorSalary)}
                      </ArgonTypography>
                      <ArgonTypography variant="body2" color="text" fontWeight="medium">
                        Total Salary: ₹ {Math.round(oldTutorTotalSalary)}
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <ArgonBox
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        p: 2.5,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                        border: "1px solid #f5f5f5",
                        height: "100%",
                      }}
                    >
                      <ArgonTypography variant="body2" color="text" fontWeight="medium">
                        Old Tutor Margin
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        {oldTutorMargin !== null ? `${oldTutorMargin}%` : "N/A"}
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <ArgonBox
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        p: 2.5,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                        border: "1px solid #f5f5f5",
                        height: "100%",
                      }}
                    >
                      <ArgonTypography variant="body2" color="text" fontWeight="medium">
                        Completed Classes (Old Tutor)
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        {oldTutorCompletedClasses}
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <ArgonBox
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        p: 2.5,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                        border: "1px solid #f5f5f5",
                        height: "100%",
                      }}
                    >
                      <ArgonTypography variant="body2" color="text" fontWeight="medium">
                        Salary Paid (Old Tutor)
                      </ArgonTypography>
                      <Chip
                        label={oldTutorSalaryStatus}
                        size="small"
                        sx={{
                          bgcolor:
                            oldTutorSalaryStatus === "PAID"
                              ? "#4caf50"
                              : oldTutorSalaryStatus === "PARTIAL"
                              ? "#ff9800"
                              : oldTutorSalaryStatus === "UNPAID"
                              ? "#f44336"
                              : "#9e9e9e",
                          color: "white",
                          fontWeight: "600",
                          mt: 1,
                        }}
                      />
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <ArgonBox
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        p: 2.5,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                        border: "1px solid #f5f5f5",
                        height: "100%",
                      }}
                    >
                      <ArgonTypography variant="body2" color="text" fontWeight="medium">
                        Tutor Replace Date
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        {oldTutorReplacedDateLabel}
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>
                </Grid>
              </ArgonBox>
            </Card>
          )}

          <Card
            sx={{
              mt: 3,
              borderRadius: "20px",
              backgroundColor: "white",
              boxShadow: "0 15px 35px rgba(0,0,0,0.08)",
              border: "1px solid #f0f0f0",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative Background Element */}
            <ArgonBox
              sx={{
                position: "absolute",
                top: -40,
                right: -40,
                width: 120,
                height: 120,
                background: "linear-gradient(45deg, #667eea, #764ba2)",
                borderRadius: "50%",
                opacity: 0.05,
              }}
            />

            <ArgonBox p={4}>
              {/* Header */}
              <ArgonBox display="flex" alignItems="center" gap={2} mb={4}>
                <ArgonBox
                  sx={{
                    background: "linear-gradient(45deg, #667eea, #764ba2)",
                    borderRadius: "12px",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                  }}
                >
                  {/* <AccountBalance sx={{ color: "white", fontSize: "22px" }} /> */}
                </ArgonBox>
                <ArgonBox>
                  <ArgonTypography variant="h5" fontWeight="bold" color="#2c3e50">
                    Credit Counter
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="#6c757d">
                    Track credits, usage and payments
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              <Grid container spacing={4}>
                {/* Progress Bar Section */}
                <Grid item xs={12} md={6}>
                  <ArgonBox
                    sx={{
                      background: "#f8f9fa",
                      borderRadius: "16px",
                      p: 3,
                      textAlign: "center",
                      border: "1px solid #e9ecef",
                    }}
                  >
                    <ArgonBox
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      mb={3}
                      justifyContent="center"
                    >
                      {/* <DonutLarge sx={{ color: "#2196F3", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                        Usage Progress
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox
                      sx={{
                        width: 180,
                        height: 180,
                        margin: "0 auto",
                        mb: 2,
                      }}
                    >
                      <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%" }}>
                        <path
                          d="M 50,50 m 0,-46 a 46,46 0 1 1 0,92 a 46,46 0 1 1 0,-92"
                          strokeWidth="6"
                          fillOpacity="0"
                          style={{ stroke: "#e0e0e0" }}
                        />
                        <path
                          d="M 50,50 m 0,-46 a 46,46 0 1 1 0,92 a 46,46 0 1 1 0,-92"
                          strokeWidth="6"
                          fillOpacity="0"
                          style={{
                            stroke: safeProgress === 0 ? "transparent" : "url(#gradient)",
                            strokeDasharray: strokeDasharray,
                            strokeDashoffset: strokeDashoffset,
                            strokeLinecap: "round",
                          }}
                        />
                        <defs>
                          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" style={{ stopColor: "#667eea", stopOpacity: 1 }} />
                            <stop offset="100%" style={{ stopColor: "#764ba2", stopOpacity: 1 }} />
                          </linearGradient>
                        </defs>
                        <text
                          x="50"
                          y="45"
                          textAnchor="middle"
                          alignmentBaseline="central"
                          style={{ fill: "#2c3e50", fontSize: "12px", fontWeight: "bold" }}
                        >
                          {safeProgress}%
                        </text>
                        <text
                          x="50"
                          y="58"
                          textAnchor="middle"
                          alignmentBaseline="central"
                          style={{ fill: "#6c757d", fontSize: "6px" }}
                        >
                          Used
                        </text>
                      </svg>
                    </ArgonBox>

                    <ArgonBox>
                      <ArgonTypography variant="h5" fontWeight="bold" color="#2c3e50">
                        ₹{usedAmount} / ₹{selectedCycleAmount}
                      </ArgonTypography>
                      <ArgonTypography variant="body2" color="#6c757d">
                        Credit Usage
                      </ArgonTypography>
                      <ArgonBox>
                        <ArgonTypography variant="caption" color="success.main" fontWeight="medium">
                          {completedSessions} / {totalSessions} Classes Completed
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Table Section */}
                <Grid item xs={12} md={6}>
                  <ArgonBox
                    sx={{
                      background: "white",
                      borderRadius: "16px",
                      p: 3,
                      boxShadow: "0 8px 25px rgba(0,0,0,0.06)",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.5} mb={3}>
                      {/* <TableChart sx={{ color: "#4CAF50", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                        Credit Details
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox
                      sx={{
                        "& .MuiTableRow-root:not(:last-child)": {
                          "& td": {
                            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                              `${borderWidth[1]} solid ${borderColor}`,
                          },
                        },
                      }}
                    >
                      <Table columns={columns1} rows={rows1} />
                    </ArgonBox>
                  </ArgonBox>
                </Grid>
              </Grid>

              {/* Balance Info Cards */}
              <Grid container spacing={3} mt={2}>
                {/* Left Balance Card */}
                <Grid item xs={12} md={6}>
                  <ArgonBox
                    sx={{
                      background: "linear-gradient(135deg, #e3f2fd 0%, #e2eaf0ff 100%)",
                      borderRadius: "16px",
                      p: 3,
                      border: "1px solid #e1f5fe",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                      {/* <AccountBalanceWallet sx={{ color: "#1976d2", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="#1976d2">
                        Account Summary
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox display="flex" flexDirection="column" gap={1.5}>
                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="#1565c0" fontWeight="medium">
                          Balance:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="bold" color="#0d47a1">
                          ₹{balanceAmount}
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="#1565c0" fontWeight="medium">
                          Unallocated Sessions:
                        </ArgonTypography>
                        <Chip
                          label={totalSessions - completedSessions}
                          size="small"
                          sx={{
                            bgcolor: "#1976d2",
                            color: "white",
                            fontWeight: "600",
                          }}
                        />
                      </ArgonBox>

                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="#1565c0" fontWeight="medium">
                          Payment Method:
                        </ArgonTypography>
                        <Chip
                          label={classData?.paymentType || "N/A"}
                          size="small"
                          sx={{
                            bgcolor: "#4caf50",
                            color: "white",
                            fontWeight: "600",
                          }}
                        />
                      </ArgonBox>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Right Balance Card */}
                <Grid item xs={12} md={6}>
                  <ArgonBox
                    sx={{
                      background: "linear-gradient(135deg, #e8f5e8 0%, #e0ede1ff 100%)",
                      borderRadius: "16px",
                      p: 3,
                      border: "1px solid #e0f2e0",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                      {/* <Payment sx={{ color: "#388e3c", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="#388e3c">
                        Payment Details
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox display="flex" flexDirection="column" gap={1.5}>
                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="#2e7d32" fontWeight="medium">
                          Available Balance:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="bold" color="#1b5e20">
                          ₹{balanceAmount}
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="#2e7d32" fontWeight="medium">
                          Payment Status:
                        </ArgonTypography>
                        <Chip
                          label={selectedPaymentStatus}
                          size="small"
                          sx={{
                            bgcolor:
                              selectedPaymentStatus === "PAID" ||
                              selectedPaymentStatus === "COMPLETED"
                                ? "#4caf50"
                                : "#ff9800",
                            fontWeight: "600",
                          }}
                        />
                      </ArgonBox>

                      <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                        <ArgonTypography variant="body2" color="#2e7d32" fontWeight="medium">
                          Total Amount:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="bold" color="#1b5e20">
                          ₹{selectedCycleAmount}
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>
              </Grid>
            </ArgonBox>
          </Card>
        </Card>
      </ArgonBox>
    </ArgonBox>
  );
}

Tables.propTypes = {
  classData: PropTypes.shape({
    completedClasses: PropTypes.number,
    totalClasses: PropTypes.number,
    totalNoOfClasses: PropTypes.number,
    feeCharge: PropTypes.number,
    tutorSalary: PropTypes.number,
    tutorPayRate: PropTypes.number,
    weeklyClasses: PropTypes.number,
    numberOfClassesInAWeek: PropTypes.number,
    duration: PropTypes.number,
    noOfMonths: PropTypes.number,
    paymentAmount: PropTypes.number,
    totalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    status: PropTypes.string,
    paymentStatus: PropTypes.string,
    paymentType: PropTypes.string,
    currentCycle: PropTypes.string,
    currentCycleName: PropTypes.string,
    sourceCycleIndex: PropTypes.number,
    sourceCycleNumber: PropTypes.number,
    monthlyTutorSalary: PropTypes.number,
    totalTutorCost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    totalTutorPay: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    grossMarginAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    grossMarginPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    className: PropTypes.string,
    preferredTime: PropTypes.string,

    invoices: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.shape({
            $oid: PropTypes.string,
          }),
        ]),
        status: PropTypes.string,
        totalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        totalClasses: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        totalMinutes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        totalTutorPay: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        tutorSalaryStatus: PropTypes.string,

        tutorPayRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        attendanceTotalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        cycleType: PropTypes.string,
        createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        updatedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      })
    ),

    board: PropTypes.shape({
      name: PropTypes.string,
    }),

    oldTutorDetails: PropTypes.shape({
      fullName: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
      profileImage: PropTypes.string,
    }),

    oldTutorPerClassSalary: PropTypes.number,
    oldTutorPaymentCycles: PropTypes.arrayOf(
      PropTypes.shape({
        cycleNumber: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      })
    ),
    oldTutorCalculatedAmount: PropTypes.number,
    oldTutorParentFeePerClass: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    oldTutorMargin: PropTypes.number,
    oldTutorCompletedClasses: PropTypes.number,
    oldTutorSalaryStatus: PropTypes.string,
    oldTutorReplacedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    oldTutorId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        _id: PropTypes.string,
      }),
    ]),

    tutor: PropTypes.shape({
      name: PropTypes.string,
      fullName: PropTypes.string,
      tutorLevel: PropTypes.string,
    }),

    parent: PropTypes.shape({
      city: PropTypes.string,
    }),

    students: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        fullName: PropTypes.string,
        className: PropTypes.string,
        class: PropTypes.shape({
          name: PropTypes.string,
        }),
        subjects: PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
              name: PropTypes.string,
            }),
          ])
        ),
        gender: PropTypes.string,
        dob: PropTypes.string,
        dateOfBirth: PropTypes.string,
        schoolName: PropTypes.string,
        school: PropTypes.shape({
          name: PropTypes.string,
        }),
        collegeName: PropTypes.string,
        boardName: PropTypes.string,
        board: PropTypes.shape({
          name: PropTypes.string,
        }),
        preferredTime: PropTypes.string,
        preferredTimeDuration: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        preferredTimeSlot: PropTypes.shape({
          name: PropTypes.string,
          label: PropTypes.string,
        }),
        preferredSlot: PropTypes.shape({
          name: PropTypes.string,
        }),
        duration: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        noOfClassesPerWeek: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        numberOfClassesInAWeek: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        noOfMonths: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        tuitionTenure: PropTypes.string,
        locationCity: PropTypes.string,
        parentId: PropTypes.shape({
          city: PropTypes.string,
        }),
        tutorLevel: PropTypes.string,
      })
    ),

    cycles: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        totalNoOfClasses: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        completedClasses: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        classes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        sessionCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        monthlyFee: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        paymentAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        tutorPay: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

        status: PropTypes.string,
        paymentStatus: PropTypes.string,
        financeStatus: PropTypes.string,

        feeStatus: PropTypes.string,
        invoiceStatus: PropTypes.string,
      })
    ),
  }),
};

export default Tables;
